import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  ForwardedRef,
} from "react";
import classNames from "classnames";
import styles from "./Dropdown.module.scss";

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: string;
  data?: { id: number | string; name: string }[];
  getClassName?: (content: { id: number | string; name: string }) => string;
  value: any;
  onChange: (...event: any[]) => void;
  name: string;
  loading?: boolean;
}

const Dropdown = forwardRef<HTMLInputElement, Props>(
  (
    { data, value, onChange, name, getClassName, error, loading, ...rest },
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const [showDropdown, setShowDropdown] = useState(false);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          wrapperRef.current &&
          !wrapperRef.current.contains(event.target as Node)
        ) {
          setShowDropdown(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    const toggleDropdown = () => {
      setShowDropdown((prev) => !prev);
    };

    const handleItemClick = (
      e: React.MouseEvent<HTMLLIElement>,
      item: { id: number | string; name: string }
    ) => {
      e.stopPropagation();
      onChange(item);
      setShowDropdown(false);
    };

    const displayValue = value?.name || "";

    return (
      <>
        <div
          ref={wrapperRef}
          className={classNames(styles.container, {
            [styles.active]: showDropdown,
            [styles.filled]: !!value?.name,
            [styles.error]: !!error,
          })}
          onClick={toggleDropdown}>
          {rest?.placeholder && (
            <label className={styles.floatingLabel}>{rest.placeholder}</label>
          )}
          <input
            ref={ref}
            name={name}
            readOnly
            value={displayValue}
            {...rest}
          />

          <img
            alt="toggle"
            src="/assets/images/svg/downArrow.svg"
            className={styles.dropdown_icon}
          />

          {showDropdown && (
            <ul className={styles.dropdown}>
              {loading
                ? // Show shimmer skeleton while loading
                  Array.from({ length: 4 }).map((_, i) => (
                    <li key={`shimmer-${i}`} className={styles.shimmerItem}>
                      <div className={styles.shimmerBar}></div>
                    </li>
                  ))
                : data?.map((item, index) => (
                    <li
                      key={index}
                      id={`dropdown-item-${index}`}
                      className={classNames(getClassName?.(item), {
                        [styles.selected]: value?.id === item.id,
                      })}
                      onClick={(e) => handleItemClick(e, item)}>
                      {item.name}
                    </li>
                  ))}
            </ul>
          )}
        </div>
        {error && <p className={styles.errorText}>{error}</p>}
      </>
    );
  }
);

Dropdown.displayName = "Dropdown";
export default Dropdown;
